import { defineStore } from "pinia";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import { SeaportService } from "~/services/seaportService";

import { BigNumber } from "ethers";

// import { MercureService } from "~/services/mercureService";

import mitt from "mitt";

import { auctionApi } from "~/api/auction";
import { OpenedPack } from "svc-auction/src/services/packs";
import { PackStockList } from "~/api/auction/types";
import { AuctionUpdate, Scarcity, UpdateStock, SocketEventType } from "fungi-types";

import { ref } from "vue";
import socketService from "~/services/socketService";

export type StoreMarketplaceEvent = {
  "update-auction": AuctionUpdate;
};

export const useStorePack = defineStore("pack-store", () => {
  const initialized = ref<boolean>(false);
  const packs = ref<PackStockList>([]);
  const buyInProgress = ref<boolean>(false);
  // const mercureService = MercureService.getInstance();
  const emitter = mitt<StoreMarketplaceEvent>();
  const selectedItem = ref<PackStockList[number] | null>(null);
  const packOpened = ref<OpenedPack | null>(null);

  const init = async () => {
    if (initialized.value) return;

    // Add Here the event listeners for cadPack Stock Update
    // mercureService.emitter.on(MercureEventType.STOCK_UPDATE, (event: UpdateStock) => {
    //   if (selectedItem.value?.id === event.dailyPackId && event.stock) {
    //     selectedItem.value.stock = event.stock;
    //   }
    // });
    socketService.on(SocketEventType.PACKS_STOCK_UPDATED, (event: UpdateStock) => {
      if (selectedItem.value?.id === event.dailyPackId && event.stock) {
        selectedItem.value.stock = event.stock;
      }
    });

    initialized.value = true;
    await listPacks();
  };

  const listPacks = async () => {
    packs.value = await auctionApi.pack.stock.list.query();
  };

  const updateStock = (event: UpdateStock) => {
    const { dailyPackId, stock } = event;
    const copyStock = [...packs.value];
    const packIndex = copyStock.findIndex((pack) => pack.id === dailyPackId);
    if (packIndex !== -1) {
      copyStock[packIndex].stock = stock;
      packs.value = copyStock;
    }
  };

  const buyPackOnChain = async (packType: Scarcity, weiAmount: BigNumber) => {
    buyInProgress.value = true;
    const buyParams = await getBuyParams(packType);
    if (!buyParams) {
      throw new Error("Failed to get buy parameters");
    }

    const pack = await auctionApi.pack.buy.onChain.mutate({
      orders: buyParams.orders.map((order) => ({
        ...order,
        orderToken: "pack",
      })),
      packId: buyParams.pacKid,
    });

    return pack;
  };

  const getBuyParams = async (packType: Scarcity) => {
    const storeUser = useStoreUserV2();
    const offerer = storeUser.walletAddress;
    const orderParams = await auctionApi.pack.orderParams.query({
      packType,
    });

    const singleOrder = JSON.parse(JSON.stringify(orderParams.orders[0]));
    singleOrder.order.offer[0].amount = BigNumber.from(singleOrder.order.offer[0].amount)
      .mul(4)
      .toString();

    const seaportService = new SeaportService(storeUser.wallet.signer);

    // Verify wallet balance by trying to get the order signature
    const orderSignature = await seaportService.getOrderSignature(singleOrder.order, offerer);

    // Create an array of promises for each card order signature
    const signedOrdersPromises = orderParams.orders.map(async (order, index) => {
      try {
        const orderSignature = await seaportService.getOrderSignature(order.order, offerer);

        // Verify signature exists
        if (!orderSignature || !orderSignature.signature) {
          console.info(`Missing signature for order at index ${index}`);
          throw new Error(`Failed to get signature for order ${index}`);
        }

        return {
          parameters: orderSignature.parameters,
          signature: orderSignature.signature,
          cuid: order.cuid,
        };
      } catch (orderError: unknown) {
        console.error(`Error signing order at index ${index}:`, orderError);
        throw orderError; // Rethrow to be caught by Promise.all
      }
    });

    // Wait for all promises to resolve
    const signedOrders = await Promise.all(signedOrdersPromises);

    // Verification step - make sure we have the same number of signed orders
    if (signedOrders.length !== orderParams.orders.length) {
      throw new Error(
        `Expected ${orderParams.orders.length} signed orders but got ${signedOrders.length}`
      );
    }

    // Additional verification - check that each order has a signature
    const missingSignatures = signedOrders.filter((order) => !order.signature).length;
    if (missingSignatures > 0) {
      throw new Error(`${missingSignatures} orders are missing signatures`);
    }

    return { orders: signedOrders, pacKid: orderParams.packId };
  };

  return {
    init,
    updateStock,
    getBuyParams,
    buyPackOnChain,
    listPacks,
    initialized,
    packs,
    buyInProgress,
    // mercureService,
    emitter,
    selectedItem,
    packOpened,
  };
});
